<template>
  <div class="adduser" style="min-height:85vh">
    <div style="margin: 16px 0; display:flex; align-items:center; flex-wrap:wrap; gap:12px">
      <a-breadcrumb>
        <a-breadcrumb-item style="font-size:30px;">{{ pageTitle }}</a-breadcrumb-item>
      </a-breadcrumb>
      <a-button @click="$router.back()">Back</a-button>
    </div>

    <a-spin :spinning="initialLoading">
      <a-card>
        <a-row :gutter="24">
          <a-col :xs="24" :md="12">
            <a-form-item label="Country" :required="true">
              <a-select
                show-search
                :value="form.countryId"
                :filter-option="filterCountryOption"
                placeholder="Select country"
                @change="handleCountryChange"
                :disabled="saving"
                style="width:100%"
              >
                <a-select-option
                  v-for="country in countries"
                  :key="country._id"
                  :value="country._id"
                >
                  {{ country.country }}
                </a-select-option>
              </a-select>
              <small v-if="form.countrySlug" class="slug-hint">
                Slug: /country/{{ form.countrySlug }}
              </small>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item label="Status" :required="true">
              <a-radio-group v-model="form.status" :disabled="saving">
                <a-radio :value="1">Published</a-radio>
                <a-radio :value="0">Draft</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <a-col :xs="24" :md="12">
            <a-form-item label="Title" :required="true">
              <a-input
                v-model="form.title"
                placeholder="Enter page title"
                :disabled="saving"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item label="Hero Image">
          <div style="display:flex; align-items:center; gap:16px; flex-wrap:wrap">
            <div v-if="form.image" style="border:1px solid #e8e8e8; padding:8px; border-radius:4px;">
              <img :src="form.image" alt="Country hero" style="max-width:220px; max-height:140px; object-fit:cover;" />
            </div>
            <a-button @click="toggleImageManager(true)" :disabled="saving">
              {{ form.image ? 'Change Image' : 'Select Image' }}
            </a-button>
            <a-button v-if="form.image" type="link" @click="clearImage" :disabled="saving">
              Remove
            </a-button>
          </div>
          <small style="display:block;margin-top:8px;color:#888;">Recommended resolution: 1600x900px or similar widescreen ratio.</small>
        </a-form-item>

        <a-modal
          v-model="imageModalVisible"
          title="Select Image"
          :footer="null"
          width="80%"
          destroy-on-close
        >
          <AwsFileManager
            v-if="imageModalVisible"
            :ismulti="false"
            @close="toggleImageManager(false)"
            @selectImages="handleImageSelect"
          />
        </a-modal>

        <a-form-item label="Description" :required="true">
          <vue2-tinymce-editor
            v-model="form.description"
            :options="tinymceOptions"
            style="min-height:400px;"
          />
        </a-form-item>

        <div style="display:flex; justify-content:flex-end; gap:12px; margin-top:20px;">
          <a-button @click="$router.push('/admin/country-content')" :disabled="saving">Cancel</a-button>
          <a-button type="primary" :loading="saving" @click="handleSubmit">
            {{ isEdit ? 'Update Country Content' : 'Create Country Content' }}
          </a-button>
        </div>
      </a-card>
    </a-spin>
  </div>
</template>

<script>
import { Vue2TinymceEditor } from "vue2-tinymce-editor";
import AwsFileManager from "../AwsFileManager.vue";
import { api } from "../../helpers/Helpers";

export default {
  components: {
    Vue2TinymceEditor,
    AwsFileManager,
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    contentId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      initialLoading: true,
      saving: false,
      imageModalVisible: false,
      countries: [],
      form: {
        countryId: undefined,
        countrySlug: "",
        title: "",
        image: "",
        description: "",
        status: 1,
      },
      tinymceOptions: {
        menubar: true,
        plugins: "advlist autolink charmap code codesample directionality emoticons image link lists",
        toolbar1:
          "fontselect | fontsizeselect | formatselect | bold italic underline strikethrough forecolor backcolor",
        toolbar2:
          "alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | link image table removeformat code",
        content_style:
          "@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400&display=swap'); body { font-family: Raleway, sans-serif; }",
        font_formats: `Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino;
Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald;
Raleway=raleway medium, raleway; Montserrat Light=Montserrat light; Montserrat Medium=Montserrat medium; Montserrat Bold=Montserrat ExtraBold;
Calibri Light=Calibri light; Calibri Medium=Calibri medium; Calibri Bold=Calibri ExtraBold; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco;
Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats`,
      },
    };
  },
  computed: {
    isEdit() {
      return this.mode === "edit";
    },
    pageTitle() {
      return this.isEdit ? "Edit Country Content" : "Add Country Content";
    },
    countryMap() {
      return this.countries.reduce((acc, cur) => {
        acc[cur._id] = cur;
        return acc;
      }, {});
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      try {
        await this.fetchCountries();
        if (this.isEdit) {
          await this.fetchContentDetails();
        }
      } catch (error) {
        this.$message.error("Failed to load country content data.");
        console.error(error);
      } finally {
        this.initialLoading = false;
      }
    },
    async fetchCountries() {
      const res = await api.getCountryInfo();
      if (Array.isArray(res)) {
        this.countries = res.sort((a, b) => (a.country || "").localeCompare(b.country || ""));
      }
    },
    async fetchContentDetails() {
      if (!this.contentId) {
        this.$message.error("Country content ID missing.");
        this.$router.replace("/admin/country-content");
        return;
      }
      const res = await api.getCountryContent(this.contentId);
      if (!res || !res._id) {
        this.$message.error("Country content not found.");
        this.$router.replace("/admin/country-content");
        return;
      }
      this.form = {
        countryId: res.country_id,
        countrySlug: res.country_slug,
        title: res.title,
        image: res.image || "",
        description: res.description || "",
        status: res.status !== undefined ? res.status : 1,
      };
    },
    filterCountryOption(input, option) {
      const text = option.componentOptions.children[0].text || "";
      return text.toLowerCase().includes(input.toLowerCase());
    },
    handleCountryChange(value) {
      this.form.countryId = value;
      const countryInfo = this.countryMap[value] || {};
      const candidate = countryInfo.slug || countryInfo.country || "";
      this.form.countrySlug = this.slugify(candidate);
    },
    toggleImageManager(visible) {
      this.imageModalVisible = visible;
    },
    handleImageSelect(images) {
      if (Array.isArray(images) && images.length > 0) {
        this.form.image = images[0];
      }
      this.toggleImageManager(false);
    },
    clearImage() {
      this.form.image = "";
    },
    slugify(value) {
      return (value || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    validate() {
      if (!this.form.countryId) {
        this.$message.warning("Please select a country.");
        return false;
      }
      if (!this.form.countrySlug) {
        this.$message.warning("Unable to derive country slug. Please re-select a country.");
        return false;
      }
      if (!this.form.title.trim()) {
        this.$message.warning("Title is required.");
        return false;
      }
      if (!this.form.description || !this.form.description.trim()) {
        this.$message.warning("Description is required.");
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        country_id: this.form.countryId,
        country_slug: this.form.countrySlug,
        title: this.form.title.trim(),
        image: this.form.image,
        description: this.form.description,
        status: Number(this.form.status),
      };
    },
    async handleSubmit() {
      if (this.saving) return;
      if (!this.validate()) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await api.updateCountryContent(this.contentId, payload);
          this.$message.success("Country content updated successfully.");
        } else {
          await api.createCountryContent(payload);
          this.$message.success("Country content created successfully.");
        }
        this.$router.push("/admin/country-content");
      } catch (error) {
        console.error(error);
        this.$message.error("Failed to save country content.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.adduser :deep(.ant-form-item-label) label {
  font-weight: 600;
}
</style>
